import React, { useState, useEffect } from 'react';
import { Container } from 'reactstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { getGcmMerchants } from '@/services/program/getGcmMerchants';
import { STATIC_CATEGORIES, CategoryTab, categoryPath } from '@/containers/Participant/GcmShared/categories';
import { YelpRating } from '@/containers/Participant/GcmShared/yelp';
import { GcmSearchBar } from '@/containers/Participant/GcmShared/GcmHeader';
import MerchantPhoto from '@/containers/Participant/GcmShared/MerchantPhoto';
import { searchPath, openBusiness, yelpBusinessUrl } from '@/containers/Participant/GcmShared/helpers';
import useGcmLocation from '@/containers/Participant/GcmShared/useGcmLocation';
import 'swiper/css';
import './index.scss';

// Section heading per card program: [lead, accent] — the accent renders orange italic
const CATEGORY_TITLES = {
    'choose_your_own_restaurant':        ['Mouthwatering', 'Local Eats'],
    'choose_your_own_dra':               ['Patio Season', 'Picks'],
    'choose_your_own_spa':               ['Relax &', 'Rejuvenate'],
    'choose_your_own_entertainment':     ['Entertainment', 'Near You'],
    'choose_your_own_best_sellers':      ['Local', 'Best Sellers'],
    'choose_your_own_home_services':     ['Local', 'Home Services'],
    'choose_your_own_auto':              ['Local', 'Auto Services'],
    'choose_your_own_personal_services': ['Local', 'Personal Services'],
};

// Cards visible in the carousel on load (up to ~4.3 per view) plus the next slide
const EAGER_PHOTOS = 6;

const GcmCategoryPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeId = searchParams.get('c') || '';
    const activeCategory = STATIC_CATEGORIES.find(c => c.id === activeId)?.key || '';

    const geo = useGcmLocation();
    const { location } = geo;
    const [searchInput, setSearchInput] = useState('');
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [swiper, setSwiper] = useState(null);

    useEffect(() => {
        if (!location) return undefined;
        let cancelled = false;
        setLoading(true);
        getGcmMerchants({ location, cardPrograms: activeCategory })
            .then(data => { if (!cancelled) setBusinesses(data.businesses || []); })
            .catch(console.error)
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [activeCategory, location]);

    const handleApply = () => {
        const query = searchInput.trim();
        if (query) navigate(searchPath(query, location));
    };

    const [titleLead, titleAccent] = CATEGORY_TITLES[activeCategory] || ['Local Eats', 'Near You'];

    return (
        <div className="gcp-page">
            <Container>
                <GcmSearchBar
                    value={searchInput}
                    onChange={setSearchInput}
                    onApply={handleApply}
                    onReset={() => setSearchInput('')}
                    geo={geo}
                    brand
                />

                {/* Shop Local Categories */}
                <section className="gcp-categories">
                    <h2 className="gcp-categories__title">Shop Local Categories</h2>
                    <div className="gcp-categories__list">
                        {STATIC_CATEGORIES.map(cat => (
                            <CategoryTab
                                key={cat.id}
                                category={cat}
                                className="gcp-category"
                                iconSize={76}
                                active={activeId === cat.id}
                                onClick={() => navigate(categoryPath(cat))}
                            />
                        ))}
                    </div>
                </section>

                <div className="gcp-panel">
                    <section className="gcp-section">
                        <h2 className="gcp-section__title">
                            {titleLead} <em className="gcp-section__accent">{titleAccent}</em>
                        </h2>
                        <p className="gcp-section__subtitle">Millions of neighborhood favorites, one text away.</p>
                        {loading ? (
                            <div className="gcp-loading">
                                <div className="spinner-border spinner-border-sm text-warning" role="status" />
                                <span>&nbsp;Loading...</span>
                            </div>
                        ) : businesses.length === 0 ? (
                            <p className="gcp-empty">No local merchants found.</p>
                        ) : (
                            <div className="gcp-carousel">
                                <button
                                    type="button"
                                    className="gcp-carousel__nav"
                                    aria-label="Previous merchants"
                                    onClick={() => swiper?.slidePrev()}
                                >
                                    <ChevronLeftIcon size={28} />
                                </button>
                                <Swiper
                                    className="gcp-carousel__track"
                                    onSwiper={setSwiper}
                                    slidesPerView={1.3}
                                    spaceBetween={16}
                                    breakpoints={{
                                        576: { slidesPerView: 2.3 },
                                        768: { slidesPerView: 3.3 },
                                        1200: { slidesPerView: 4.3, spaceBetween: 20 },
                                    }}
                                >
                                    {businesses.map((b, i) => (
                                        <SwiperSlide key={b.id}>
                                            <div className="gcp-card">
                                                <button type="button" className="gcm-card-open" onClick={() => openBusiness(navigate, b)}>
                                                    <div className="gcp-card__img">
                                                        <MerchantPhoto url={b.image_url} alt={b.name} placeholderClassName="gcp-card__img--placeholder" eager={i < EAGER_PHOTOS} />
                                                    </div>
                                                    <p className="gcp-card__name">{b.name}</p>
                                                </button>
                                                {b.rating > 0 && <YelpRating rating={b.rating} reviewCount={b.review_count} href={yelpBusinessUrl(b)} />}
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <button
                                    type="button"
                                    className="gcp-carousel__nav"
                                    aria-label="Next merchants"
                                    onClick={() => swiper?.slideNext()}
                                >
                                    <ChevronRightIcon size={28} />
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </Container>
        </div>
    );
};

export default GcmCategoryPage;
